import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

const footerLinks = [
  {
    heading: 'Discover',
    links: [
      { label: 'Why Panorama', href: '/why-panorama' },
      { label: 'Amenities', href: '/amenities' },
      { label: 'Lifestyle', href: '/lifestyle' },
      { label: 'Community', href: '/community' },
      { label: 'Nearby', href: '/nearby' },
      { label: 'Real Estate', href: '/real-estate' },
      { label: 'FAQ', href: '/faq' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Locals Guide', href: '/resources/locals-guide' },
      { label: 'Panorama Living', href: '/resources/panorama-living' },
      { label: 'Perks & Privileges', href: '/resources/perks' },
      { label: 'Real Estate Resources', href: '/resources/real-estate' },
      { label: 'All Resources', href: '/resources' },
    ],
  },
  {
    heading: 'Community',
    links: [
      { label: 'Directory', href: '/directory' },
      { label: 'Conversations', href: '/conversations' },
      { label: 'Events', href: '/events' },
      { label: 'Marketplace', href: '/marketplace' },
      { label: 'Services', href: '/services' },
      { label: 'Newsletter', href: '/newsletter' },
    ],
  },
  {
    heading: 'Platform',
    links: [
      { label: 'Request Access', href: '/request-access' },
      { label: 'Verification Status', href: '/verification/pending' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Use', href: '/terms-of-use' },
      { label: 'Community Guidelines', href: '/community-guidelines' },
    ],
  },
];

export default function SiteFooter() {
  return (
    <footer style={{ background: '#07060C' }}>
      {/* Neon divider — top of footer */}
      <div className="neon-divider" />
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-5">
              <AppLogo size={32} />
              <div className="flex flex-col leading-none">
                <span
                  className="font-display font-bold text-[15px] tracking-tight"
                  style={{ color: '#F2F4FF' }}
                >
                  Panorama
                </span>
                <span
                  className="text-[10px] font-medium tracking-[0.12em] uppercase"
                  style={{ color: 'rgba(167,163,196,0.45)' }}
                >
                  Towers
                </span>
              </div>
            </div>
            <p
              className="text-[13.5px] leading-relaxed max-w-xs"
              style={{ color: 'rgba(167,163,196,0.65)' }}
            >
              A private resident network for Panorama Towers — the landmark twin high-rise community on Dean Martin Drive, Las Vegas.
            </p>
            <p
              className="mt-4 text-[11px] leading-relaxed max-w-xs"
              style={{ color: 'rgba(167,163,196,0.30)' }}
            >
              This platform is resident-operated and not affiliated with Panorama Towers management, the HOA, or any official property entity.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <Link href="/request-access" className="btn-primary group inline-flex text-[13px] py-2.5 px-4">
                Request Access
              </Link>
              <Link
                href="/why-panorama"
                className="text-[12px] font-medium transition-colors duration-150 hover:opacity-80"
                style={{ color: 'rgba(167,163,196,0.55)' }}
              >
                Learn about Panorama →
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full animate-glow-pulse"
                style={{ background: '#4CC9FF' }}
              />
              <span
                className="text-[11px] tracking-wide"
                style={{ color: 'rgba(167,163,196,0.35)' }}
              >
                4525 &amp; 4575 Dean Martin Drive · Las Vegas, NV 89103
              </span>
            </div>
          </div>

          {/* Link columns */}
          {footerLinks?.map((col) => (
            <div key={`footer-col-${col?.heading}`}>
              <h4
                className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-4"
                style={{ color: 'rgba(167,163,196,0.40)' }}
              >
                {col?.heading}
              </h4>
              <ul className="space-y-2.5">
                {col?.links?.map((link) => (
                  <li key={`footer-link-${link?.label}`}>
                    <Link
                      href={link?.href}
                      className="footer-link text-[13.5px] transition-colors duration-150"
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div
          className="mt-14 pt-6 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          style={{ borderColor: 'rgba(255,255,255,0.06)' }}
        >
          <p
            className="text-[12px]"
            style={{ color: 'rgba(167,163,196,0.30)' }}
          >
            © 2026 Panorama Community Network. All rights reserved.
          </p>
          <p
            className="text-[11px]"
            style={{ color: 'rgba(167,163,196,0.25)' }}
          >
            Built by{' '}
            <a
              href="https://bestoddscorp.com/work-with-daniel"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-70 transition-opacity"
            >
              Daniel Moravec
            </a>
            {' '}·{' '}
            <a
              href="https://bestoddscorp.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-70 transition-opacity"
            >
              BestOddsCorp.com
            </a>
          </p>
          <p
            className="text-[11px] italic"
            style={{ color: 'rgba(167,163,196,0.20)' }}
          >
            Dev note: Official Panorama Towers wordmark and logo assets pending. Placeholder branding in use.
          </p>
        </div>
      </div>
    </footer>
  );
}